import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Card from '../components/ui/Card';
import DateSelect from '../components/ui/DateSelect';
import Button from '../components/ui/Button';
import ProgrammeService from '../services/ProgrammeService';
import StaffService from '../services/StaffService';
import EmailService from '../services/EmailService';
import MessageService from '../services/MessageService';
import RoleSettingsService from '../services/RoleSettingsService';
import { buildPasswordHash, buildResetToken } from '../services/PasswordService';
import { formatDateTime } from '../utils/dateFormat';
import { getStaffPortalLoginUrl } from '../utils/loginLinks';

const buildCertificate = () => ({
  id: `cert-${Math.random().toString(36).slice(2, 8)}`,
  name: '',
  expiryDate: '',
  files: []
});

const buildLeaveEntry = () => ({
  id: `leave-${Math.random().toString(36).slice(2, 8)}`,
  startDate: '',
  endDate: '',
  notes: ''
});

const roleOptions = [
  'Admin',
  'Manager',
  'Office Staff',
  'Safeguarding Assistant',
  'Senior Support Worker',
  'Support Worker',
  'Teacher',
  'Mentor',
  'Youth Mentor',
  'Volunteer',
  'Outreach Worker',
  'Programme Lead'
];

const accessScopeOptions = [
  { id: 'admin', label: 'Admin (full access)' },
  { id: 'staff', label: 'Staff (assigned students only)' },
  { id: 'inventory', label: 'Inventory only' }
];

const countLeaveDays = (entries) =>
  entries.reduce((sum, entry) => {
    if (!entry.startDate) return sum;
    const start = new Date(entry.startDate);
    const end = entry.endDate ? new Date(entry.endDate) : start;
    const diff = Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1);
    return sum + (Number.isNaN(diff) ? 1 : diff);
  }, 0);

const safeList = (service) => {
  if (service && typeof service.list === 'function') return service.list();
  return [];
};

export default function StaffFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const programmeService = useMemo(() => new ProgrammeService(), []);
  const staffService = useMemo(() => new StaffService(), []);
  const emailService = useMemo(() => new EmailService(), []);
  const messageService = useMemo(() => new MessageService(), []);
  const roleService = useMemo(() => new RoleSettingsService(), []);
  const programmes = useMemo(() => safeList(programmeService), [programmeService]);
  const accessRoleOptions = useMemo(() => safeList(roleService), [roleService]);
  const existingStaff = id ? staffService.getById(id) : null;
  const isEdit = Boolean(existingStaff);
  const initRef = useRef(false);

  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [addressLines, setAddressLines] = useState(['', '', '', '']);
  const [status, setStatus] = useState('active');
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState('');
  const [programmeIds, setProgrammeIds] = useState([]);
  const [certificates, setCertificates] = useState([buildCertificate()]);
  const [sickEntries, setSickEntries] = useState([buildLeaveEntry()]);
  const [holidayEntries, setHolidayEntries] = useState([buildLeaveEntry()]);
  const [loginEnabled, setLoginEnabled] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [accessScope, setAccessScope] = useState('staff');
  const [roleKey, setRoleKey] = useState('teacher');
  const [formError, setFormError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [passwordMode, setPasswordMode] = useState('email');
  const [sendSetPasswordEmail, setSendSetPasswordEmail] = useState(true);
  const [tempPassword, setTempPassword] = useState('');
  const [passwordMustChange, setPasswordMustChange] = useState(true);
  const [sendTempPasswordEmail, setSendTempPasswordEmail] = useState(true);
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordMustChange, setNewPasswordMustChange] = useState(true);
  const [sendNewPasswordEmail, setSendNewPasswordEmail] = useState(false);
  const [resetStatus, setResetStatus] = useState('');

  useEffect(() => {
    initRef.current = false;
  }, [id]);

  useEffect(() => {
    if (!existingStaff || initRef.current) return;
    initRef.current = true;
    setFirstName(existingStaff.firstName || existingStaff.name?.split(' ')[0] || '');
    setMiddleName(existingStaff.middleName || '');
    setLastName(existingStaff.lastName || existingStaff.name?.split(' ').slice(1).join(' ') || '');
    setRole(existingStaff.role || '');
    setEmail(existingStaff.email || '');
    setPhone(existingStaff.phone || '');
    setStatus(existingStaff.status || 'active');
    setStartDate(existingStaff.startDate || new Date().toISOString().slice(0, 10));
    setEndDate(existingStaff.endDate || '');
    const nextAddress = Array.isArray(existingStaff.address) ? [...existingStaff.address] : [];
    while (nextAddress.length < 4) nextAddress.push('');
    setAddressLines(nextAddress.slice(0, 4));
    setProgrammeIds(existingStaff.programmes || existingStaff.programmeIds || []);
    setLoginEnabled(Boolean(existingStaff.loginEnabled));
    setLoginEmail(existingStaff.loginEmail || existingStaff.email || '');
    setAccessScope(existingStaff.accessScope || 'staff');
    setRoleKey(existingStaff.roleKey || (existingStaff.accessScope === 'admin' ? 'admin' : existingStaff.accessScope === 'inventory' ? 'inventory' : 'teacher'));
    setPasswordMode('email');
    setSendSetPasswordEmail(true);
    setTempPassword('');
    setPasswordMustChange(Boolean(existingStaff.passwordMustChange));
    setSendTempPasswordEmail(true);
    setNewPassword('');
    setNewPasswordMustChange(true);
    setSendNewPasswordEmail(false);

    const certs =
      existingStaff.certificates?.length
        ? existingStaff.certificates
        : (existingStaff.documents || []).map((doc) => ({
            id: doc.id || `cert-${Math.random().toString(36).slice(2, 8)}`,
            name: doc.name || 'Certificate',
            expiryDate: doc.expiry || '',
            files: doc.files || []
          }));
    setCertificates(certs.length ? certs : [buildCertificate()]);
    setSickEntries(existingStaff.sickEntries?.length ? existingStaff.sickEntries : [buildLeaveEntry()]);
    setHolidayEntries(existingStaff.holidayEntries?.length ? existingStaff.holidayEntries : [buildLeaveEntry()]);
  }, [existingStaff]);

  if (id && !existingStaff) {
    return (
      <Card className="p-6">
        <p className="sp-muted">Staff member not found.</p>
        <Button variant="secondary" className="mt-4" onClick={() => navigate('/support-workers')}>
          Back to Support Workers
        </Button>
      </Card>
    );
  }

  const updateAddressLine = (index, value) => {
    setAddressLines((prev) => prev.map((line, idx) => (idx === index ? value : line)));
  };

  const toggleProgramme = (id) => {
    setProgrammeIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const updateCertificate = (id, updates) => {
    setCertificates((prev) => prev.map((cert) => (cert.id === id ? { ...cert, ...updates } : cert)));
  };

  const addCertificate = () => setCertificates((prev) => [...prev, buildCertificate()]);
  const removeCertificate = (id) => setCertificates((prev) => prev.filter((cert) => cert.id !== id));

  const updateLeaveEntry = (type, id, updates) => {
    const updater = type === 'sick' ? setSickEntries : setHolidayEntries;
    updater((prev) => prev.map((entry) => (entry.id === id ? { ...entry, ...updates } : entry)));
  };

  const addLeaveEntry = (type) => {
    const updater = type === 'sick' ? setSickEntries : setHolidayEntries;
    updater((prev) => [...prev, buildLeaveEntry()]);
  };

  const removeLeaveEntry = (type, id) => {
    const updater = type === 'sick' ? setSickEntries : setHolidayEntries;
    updater((prev) => prev.filter((entry) => entry.id !== id));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setFormError('');
    setInfoMessage('');
    if (!firstName.trim() || !lastName.trim() || !role.trim()) {
      setFormError('First name, last name, and role are required.');
      return;
    }
    if (loginEnabled && !loginEmail.trim() && !email.trim()) {
      setFormError('Provide a login email when enabling access.');
      return;
    }
    if (loginEnabled && !roleKey) {
      setFormError('Select an access role for this login.');
      return;
    }

    const loginEmailValue = (loginEmail.trim() || email.trim()).toLowerCase();
    const existingLogin = safeList(staffService).find(
      (staff) => staff.loginEmail?.toLowerCase() === loginEmailValue && staff.id !== existingStaff?.id
    );
    if (loginEnabled && existingLogin) {
      setFormError('That login email is already assigned to another staff member.');
      return;
    }

    if (!isEdit && loginEnabled && passwordMode === 'temp' && !tempPassword.trim()) {
      setFormError('Enter a temporary password or switch to email setup.');
      return;
    }
    if (loginEnabled && isEdit && newPassword && !newPassword.trim()) {
      setFormError('Enter a valid password or leave the field blank.');
      return;
    }

    const fullName = [firstName, middleName, lastName].filter(Boolean).join(' ').trim();
    const cleanedCertificates = certificates
      .filter((cert) => cert.name.trim() || cert.files.length || cert.expiryDate)
      .map((cert) => ({
        ...cert,
        name: cert.name.trim() || 'Certificate',
        expiryDate: cert.expiryDate || '',
        files: cert.files
      }));

    const cleanedSick = sickEntries.filter((entry) => entry.startDate || entry.endDate || entry.notes);
    const cleanedHoliday = holidayEntries.filter((entry) => entry.startDate || entry.endDate || entry.notes);

    const payload = {
      ...(existingStaff || {}),
      id: existingStaff?.id || `staff-${Math.random().toString(36).slice(2, 8)}`,
      name: fullName,
      firstName: firstName.trim(),
      middleName: middleName.trim(),
      lastName: lastName.trim(),
      role: role.trim(),
      email: email.trim().toLowerCase(),
      phone: phone.trim(),
      address: addressLines.map((line) => line.trim()).filter(Boolean),
      startDate,
      endDate: endDate || null,
      status,
      programmes: programmeIds,
      certificates: cleanedCertificates,
      documents: cleanedCertificates.map((cert) => ({
        id: cert.id,
        name: cert.name,
        expiry: cert.expiryDate,
        status: cert.expiryDate ? 'expiring' : 'valid',
        files: cert.files
      })),
      sickEntries: cleanedSick,
      holidayEntries: cleanedHoliday,
      sickDays: { used: countLeaveDays(cleanedSick), total: 10 },
      holidays: { used: countLeaveDays(cleanedHoliday), total: 28 },
      hoursThisWeek: 0,
      statusHistory: existingStaff?.statusHistory || []
    };

    payload.loginEnabled = loginEnabled;
    payload.loginEmail = (loginEmail.trim() || payload.email).toLowerCase();
    payload.accessScope = loginEnabled ? accessScope : 'staff';
    payload.roleKey = loginEnabled ? roleKey : payload.roleKey || roleKey;
    payload.passwordHash = existingStaff?.passwordHash || null;
    payload.passwordMustChange = Boolean(existingStaff?.passwordMustChange);
    payload.passwordResetTokenHash = existingStaff?.passwordResetTokenHash || null;
    payload.passwordResetExpiresAt = existingStaff?.passwordResetExpiresAt || null;
    payload.lastPasswordResetAt = existingStaff?.lastPasswordResetAt || null;

    if (loginEnabled && !isEdit && passwordMode === 'temp') {
      payload.passwordHash = await buildPasswordHash(tempPassword.trim());
      payload.passwordMustChange = Boolean(passwordMustChange);
      payload.passwordResetTokenHash = null;
      payload.passwordResetExpiresAt = null;
      payload.lastPasswordResetAt = null;
    }
    if (loginEnabled && isEdit && newPassword.trim()) {
      payload.passwordHash = await buildPasswordHash(newPassword.trim());
      payload.passwordMustChange = Boolean(newPasswordMustChange);
      payload.passwordResetTokenHash = null;
      payload.passwordResetExpiresAt = null;
      payload.lastPasswordResetAt = new Date().toISOString();
    }

    if (isEdit) {
      staffService.update(payload);
    } else {
      staffService.add(payload);
    }

    const postSaveNotices = [];
    const staffPortalLoginUrl = getStaffPortalLoginUrl();

    if (loginEnabled && !isEdit && passwordMode === 'email' && sendSetPasswordEmail) {
      const { token, tokenHash, expiresAt } = await buildResetToken();
      const updated = { ...payload };
      updated.passwordResetTokenHash = tokenHash;
      updated.passwordResetExpiresAt = expiresAt;
      updated.lastPasswordResetAt = new Date().toISOString();
      staffService.update(updated);

      const actionUrl = typeof window !== 'undefined'
        ? `${window.location.origin}/reset-password?token=${token}`
        : `/reset-password?token=${token}`;
      const emailLog = emailService.sendTemplate({
        templateKey: 'staff.set_password',
        to: updated.loginEmail,
        data: {
          staff_name: updated.name,
          action_url: actionUrl,
          login_url: staffPortalLoginUrl,
          site_name: 'SafeguardPro'
        },
        entity: { type: 'staff', id: updated.id }
      });
      if (emailLog.status === 'sent') {
        postSaveNotices.push('Set-password email sent.');
      } else {
        postSaveNotices.push(`Set-password email failed: ${emailLog.error_message || 'Check SMTP settings.'}`);
      }
    }

    if (loginEnabled && !isEdit && passwordMode === 'temp' && sendTempPasswordEmail) {
      const emailLog = emailService.sendTemplate({
        templateKey: 'staff.temp_password',
        to: payload.loginEmail,
        data: {
          staff_name: payload.name,
          login_email: payload.loginEmail,
          temp_password: tempPassword.trim(),
          login_url: staffPortalLoginUrl,
          site_name: 'SafeguardPro'
        },
        entity: { type: 'staff', id: payload.id }
      });
      if (emailLog.status === 'sent') {
        postSaveNotices.push('Temporary password email sent.');
      } else {
        postSaveNotices.push(`Temporary password email failed: ${emailLog.error_message || 'Check SMTP settings.'}`);
      }
    }
    if (loginEnabled && isEdit && newPassword.trim() && sendNewPasswordEmail) {
      const emailLog = emailService.sendTemplate({
        templateKey: 'staff.temp_password',
        to: payload.loginEmail,
        data: {
          staff_name: payload.name,
          login_email: payload.loginEmail,
          temp_password: newPassword.trim(),
          login_url: staffPortalLoginUrl,
          site_name: 'SafeguardPro'
        },
        entity: { type: 'staff', id: payload.id }
      });
      if (emailLog.status === 'sent') {
        postSaveNotices.push('Updated password email sent.');
      } else {
        postSaveNotices.push(`Password email failed: ${emailLog.error_message || 'Check SMTP settings.'}`);
      }
    }

    if (loginEnabled && !isEdit) {
      const onboardingBody = [
        `Hi ${payload.name},`,
        'Your SafeguardPro staff access has been set up.',
        `Staff portal login: ${staffPortalLoginUrl}`,
        passwordMode === 'email'
          ? 'Check your email for the set-password link before signing in.'
          : 'Use your temporary password from onboarding and change it at first sign-in.'
      ].join('\n\n');
      messageService.send({
        subject: 'Your staff login is ready',
        body: onboardingBody,
        fromUser: { id: 'system', name: 'SafeguardPro' },
        recipients: [{ id: payload.id, name: payload.name, type: 'staff' }],
        references: [
          {
            type: 'staff',
            id: payload.id,
            label: payload.name,
            route: `/support-workers/${payload.id}`
          }
        ]
      });
      postSaveNotices.push('Internal onboarding message sent.');
    }

    navigate('/support-workers', {
      state: {
        savedMessage: [isEdit ? 'Staff profile updated.' : 'Staff member created.', ...postSaveNotices].join(' ').trim()
      }
    });
  };

  const handleResetPassword = async () => {
    if (!existingStaff) return;
    const targetEmail = loginEmail.trim() || existingStaff.loginEmail || existingStaff.email;
    if (!targetEmail) {
      setResetStatus('No login email configured.');
      return;
    }
    setResetStatus('');
    const { token, tokenHash, expiresAt } = await buildResetToken();
    const nextStaff = {
      ...existingStaff,
      passwordResetTokenHash: tokenHash,
      passwordResetExpiresAt: expiresAt,
      lastPasswordResetAt: new Date().toISOString()
    };
    staffService.update(nextStaff);
    const actionUrl = typeof window !== 'undefined'
      ? `${window.location.origin}/reset-password?token=${token}`
      : `/reset-password?token=${token}`;
    const emailLog = emailService.sendTemplate({
      templateKey: 'staff.reset_password',
      to: targetEmail,
      data: {
        staff_name: nextStaff.name,
        action_url: actionUrl,
        expires_at: formatDateTime(expiresAt),
        site_name: 'SafeguardPro'
      },
      entity: { type: 'staff', id: nextStaff.id }
    });
    if (emailLog.status === 'sent') {
      setResetStatus('Password reset email sent.');
      return;
    }
    setResetStatus(`Password reset email failed: ${emailLog.error_message || 'Check SMTP settings.'}`);
  };

  return (
    <form className="space-y-6" onSubmit={handleSave}>
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>
            {isEdit ? 'Edit Staff Member' : 'Add Staff Member'}
          </h2>
          <p className="sp-muted">
            {isEdit ? 'Update the staff profile and assignments.' : 'Create a new support worker profile.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" type="button" onClick={() => navigate('/support-workers')}>
            Cancel
          </Button>
          <Button size="sm" type="submit">{isEdit ? 'Save Changes' : 'Save Staff'}</Button>
        </div>
      </div>

      {formError && (
        <div className="sp-alert sp-alert--critical text-sm">
          {formError}
        </div>
      )}
      {infoMessage && (
        <div className="sp-alert sp-alert--success text-sm">
          {infoMessage}
        </div>
      )}

      <Card className="p-5 space-y-4">
        <div>
          <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Basic Information</h3>
          <p className="text-sm sp-muted">Core staff details and contact information.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="sp-label">First name</label>
            <input className="sp-input mt-2 w-full" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </div>
          <div>
            <label className="sp-label">Middle name</label>
            <input className="sp-input mt-2 w-full" value={middleName} onChange={(e) => setMiddleName(e.target.value)} />
          </div>
          <div>
            <label className="sp-label">Surname</label>
            <input className="sp-input mt-2 w-full" value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </div>
          <div>
            <label className="sp-label">Role</label>
            <select className="sp-select mt-2 w-full" value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="">Select role</option>
              {roleOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="sp-label">Email</label>
            <input type="email" className="sp-input mt-2 w-full" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <label className="sp-label">Phone</label>
            <input className="sp-input mt-2 w-full" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div>
            <label className="sp-label">Status</label>
            <select className="sp-select mt-2 w-full" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div>
            <label className="sp-label">Start date</label>
            <DateSelect value={startDate} onChange={setStartDate} className="mt-2 w-full" />
          </div>
          <div>
            <label className="sp-label">End date</label>
            <DateSelect value={endDate} onChange={setEndDate} className="mt-2 w-full" />
          </div>
        </div>
        <div>
          <label className="sp-label">Address</label>
          <div className="grid md:grid-cols-2 gap-3 mt-2">
            {addressLines.map((line, index) => (
              <input
                key={`address-${index}`}
                className="sp-input w-full"
                placeholder={`Address line ${index + 1}`}
                value={line}
                onChange={(e) => updateAddressLine(index, e.target.value)}
              />
            ))}
          </div>
        </div>
      </Card>

      <Card className="p-5 space-y-4">
        <div>
          <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Login & Access</h3>
          <p className="text-sm sp-muted">Enable sign-in and control what this staff member can see.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="sp-label">Enable login</label>
            <label className="sp-inline-toggle mt-2">
              <input
                type="checkbox"
                checked={loginEnabled}
                onChange={(event) => setLoginEnabled(event.target.checked)}
              />
              Allow access to the SafeguardPro workspace
            </label>
          </div>
          <div>
            <label className="sp-label">Login email</label>
            <input
              className="sp-input mt-2 w-full"
              value={loginEmail}
              onChange={(event) => setLoginEmail(event.target.value)}
              placeholder="name@centre.org"
              type="email"
              disabled={!loginEnabled}
            />
          </div>
          <div>
            <label className="sp-label">Access scope</label>
            <select
              className="sp-select mt-2 w-full"
              value={accessScope}
              onChange={(event) => setAccessScope(event.target.value)}
              disabled={!loginEnabled}
            >
              {accessScopeOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="sp-label">Access role</label>
            <select
              className="sp-select mt-2 w-full"
              value={roleKey}
              onChange={(event) => setRoleKey(event.target.value)}
              disabled={!loginEnabled}
            >
              {accessRoleOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
            <div className="text-xs sp-muted mt-1">Controls which pages and modules this user can access.</div>
          </div>
        </div>
        {loginEnabled && !isEdit && (
          <div className="mt-4 space-y-3">
            <div className="sp-label">Password setup</div>
            <div className="grid md:grid-cols-2 gap-3 text-sm">
              <label className="sp-panel sp-panel--muted p-3 flex items-start gap-2">
                <input
                  type="radio"
                  name="passwordMode"
                  checked={passwordMode === 'email'}
                  onChange={() => setPasswordMode('email')}
                />
                <span>
                  <span className="sp-value" style={{ fontWeight: 600 }}>Send set‑password email</span>
                  <span className="text-xs sp-muted block">Staff choose their own password via a secure link.</span>
                </span>
              </label>
              <label className="sp-panel sp-panel--muted p-3 flex items-start gap-2">
                <input
                  type="radio"
                  name="passwordMode"
                  checked={passwordMode === 'temp'}
                  onChange={() => setPasswordMode('temp')}
                />
                <span>
                  <span className="sp-value" style={{ fontWeight: 600 }}>Set temporary password</span>
                  <span className="text-xs sp-muted block">Admin sets a temporary password (change required).</span>
                </span>
              </label>
            </div>
            {passwordMode === 'email' && (
              <label className="sp-inline-toggle">
                <input
                  type="checkbox"
                  checked={sendSetPasswordEmail}
                  onChange={(event) => setSendSetPasswordEmail(event.target.checked)}
                />
                Send set‑password email after saving
              </label>
            )}
            {passwordMode === 'temp' && (
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="sp-label">Temporary password</label>
                  <input
                    className="sp-input mt-2 w-full"
                    type="password"
                    value={tempPassword}
                    onChange={(event) => setTempPassword(event.target.value)}
                    placeholder="Enter temporary password"
                  />
                  <div className="text-xs sp-muted mt-1">
                    Share securely. Avoid sending passwords in email unless required by policy.
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-7">
                  <label className="sp-inline-toggle">
                    <input
                      type="checkbox"
                      checked={passwordMustChange}
                      onChange={(event) => setPasswordMustChange(event.target.checked)}
                    />
                    Require change on first login
                  </label>
                </div>
                <div className="md:col-span-2">
                  <label className="sp-inline-toggle">
                    <input
                      type="checkbox"
                      checked={sendTempPasswordEmail}
                      onChange={(event) => setSendTempPasswordEmail(event.target.checked)}
                    />
                    Email temporary password to staff member
                  </label>
                  <div className="text-xs sp-muted mt-1">
                    Uses the login email configured above. Consider using the set‑password link where possible.
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {loginEnabled && isEdit && (
          <div className="mt-4 space-y-3">
            <div className="sp-label">Update password</div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="sp-label">New password</label>
                <input
                  className="sp-input mt-2 w-full"
                  type="password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  placeholder="Leave blank to keep current password"
                />
                <div className="text-xs sp-muted mt-1">
                  Setting a password here replaces the existing one immediately.
                </div>
              </div>
              <div className="flex flex-col justify-center gap-2">
                <label className="sp-inline-toggle">
                  <input
                    type="checkbox"
                    checked={newPasswordMustChange}
                    onChange={(event) => setNewPasswordMustChange(event.target.checked)}
                    disabled={!newPassword}
                  />
                  Require change on next login
                </label>
                <label className="sp-inline-toggle">
                  <input
                    type="checkbox"
                    checked={sendNewPasswordEmail}
                    onChange={(event) => setSendNewPasswordEmail(event.target.checked)}
                    disabled={!newPassword}
                  />
                  Email new password to staff
                </label>
                <div className="text-xs sp-muted">
                  Prefer the reset link if policy discourages sending passwords.
                </div>
              </div>
            </div>
            <div className="sp-divider" />
            <div className="sp-label">Password reset</div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button size="sm" type="button" variant="secondary" onClick={handleResetPassword}>
                Send reset link
              </Button>
              {existingStaff?.lastPasswordResetAt && (
                <span className="text-xs sp-muted">
                  Last sent {formatDateTime(existingStaff.lastPasswordResetAt)}
                </span>
              )}
              {resetStatus && <span className="text-xs sp-muted">{resetStatus}</span>}
            </div>
          </div>
        )}
      </Card>

      <Card className="p-5 space-y-4">
        <div>
          <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Programme Assignments</h3>
          <p className="text-sm sp-muted">Assign staff to current programmes and activities.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-2">
          {programmes.map((programme) => (
            <label key={programme.id} className="flex items-center gap-2 text-sm sp-muted">
              <input
                type="checkbox"
                checked={programmeIds.includes(programme.id)}
                onChange={() => toggleProgramme(programme.id)}
              />
              <span>{programme.name} · {programme.day || programme.days?.join(', ') || 'TBD'}</span>
            </label>
          ))}
          {programmes.length === 0 && (
            <p className="text-sm sp-muted">No programmes available.</p>
          )}
        </div>
      </Card>

      <Card className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Certificates & Attachments</h3>
            <p className="text-sm sp-muted">Upload certificates and set expiry dates for alerts.</p>
          </div>
          <Button size="sm" variant="secondary" type="button" onClick={addCertificate}>
            Add certificate
          </Button>
        </div>
        {certificates.map((cert) => (
          <div key={cert.id} className="grid md:grid-cols-[2fr_1fr_1fr_auto] gap-3 items-end">
            <div>
              <label className="sp-label">Certificate name</label>
              <input
                className="sp-input mt-2 w-full"
                value={cert.name}
                onChange={(e) => updateCertificate(cert.id, { name: e.target.value })}
              />
            </div>
            <div>
              <label className="sp-label">Expiry date</label>
              <DateSelect
                value={cert.expiryDate}
                onChange={(value) => updateCertificate(cert.id, { expiryDate: value })}
                className="mt-2 w-full"
              />
            </div>
            <div>
              <label className="sp-label">Attachments</label>
              <input
                type="file"
                multiple
                className="sp-input mt-2 w-full"
                onChange={(e) =>
                  updateCertificate(cert.id, { files: Array.from(e.target.files || []).map((file) => file.name) })
                }
              />
              {cert.files.length > 0 && (
                <div className="text-xs sp-muted mt-1">{cert.files.join(', ')}</div>
              )}
            </div>
            <div>
              <Button size="sm" variant="ghost" type="button" onClick={() => removeCertificate(cert.id)}>
                Remove
              </Button>
            </div>
          </div>
        ))}
      </Card>

      <Card className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Sick Days</h3>
            <p className="text-sm sp-muted">Track sick leave entries for this staff member.</p>
          </div>
          <Button size="sm" variant="secondary" type="button" onClick={() => addLeaveEntry('sick')}>
            Add sick entry
          </Button>
        </div>
        {sickEntries.map((entry) => (
          <div key={entry.id} className="grid md:grid-cols-[1fr_1fr_2fr_auto] gap-3 items-end">
            <div>
              <label className="sp-label">Start date</label>
              <DateSelect
                value={entry.startDate}
                onChange={(value) => updateLeaveEntry('sick', entry.id, { startDate: value })}
                className="mt-2 w-full"
              />
            </div>
            <div>
              <label className="sp-label">End date</label>
              <DateSelect
                value={entry.endDate}
                onChange={(value) => updateLeaveEntry('sick', entry.id, { endDate: value })}
                className="mt-2 w-full"
              />
            </div>
            <div>
              <label className="sp-label">Notes</label>
              <input
                className="sp-input mt-2 w-full"
                value={entry.notes}
                onChange={(e) => updateLeaveEntry('sick', entry.id, { notes: e.target.value })}
              />
            </div>
            <div>
              <Button size="sm" variant="ghost" type="button" onClick={() => removeLeaveEntry('sick', entry.id)}>
                Remove
              </Button>
            </div>
          </div>
        ))}
      </Card>

      <Card className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Holiday Days</h3>
            <p className="text-sm sp-muted">Track holiday leave entries for this staff member.</p>
          </div>
          <Button size="sm" variant="secondary" type="button" onClick={() => addLeaveEntry('holiday')}>
            Add holiday entry
          </Button>
        </div>
        {holidayEntries.map((entry) => (
          <div key={entry.id} className="grid md:grid-cols-[1fr_1fr_2fr_auto] gap-3 items-end">
            <div>
              <label className="sp-label">Start date</label>
              <DateSelect
                value={entry.startDate}
                onChange={(value) => updateLeaveEntry('holiday', entry.id, { startDate: value })}
                className="mt-2 w-full"
              />
            </div>
            <div>
              <label className="sp-label">End date</label>
              <DateSelect
                value={entry.endDate}
                onChange={(value) => updateLeaveEntry('holiday', entry.id, { endDate: value })}
                className="mt-2 w-full"
              />
            </div>
            <div>
              <label className="sp-label">Notes</label>
              <input
                className="sp-input mt-2 w-full"
                value={entry.notes}
                onChange={(e) => updateLeaveEntry('holiday', entry.id, { notes: e.target.value })}
              />
            </div>
            <div>
              <Button size="sm" variant="ghost" type="button" onClick={() => removeLeaveEntry('holiday', entry.id)}>
                Remove
              </Button>
            </div>
          </div>
        ))}
      </Card>
    </form>
  );
}
